import React, { Component } from "react";
import { getActiveBranches } from "../Endpoint/Admin";
import {
  isBranchScopedAdmin,
  getUserBranchName,
  getUserBranchId,
  getActiveBranchId,
} from "../services/PermissionService";

/**
 * Always-visible statement of which branch the admin panel is currently acting
 * on, shown in the header next to the hotel name.
 *
 * The branch switcher already carries this, but it reads as a control rather
 * than a status, and a branch-scoped admin has no switcher at all. Every list,
 * price and order in the panel is filtered by this value, so it is worth
 * stating plainly rather than leaving someone to infer it from a dropdown.
 *
 *   branch-scoped admin -> their own branch (they cannot change it)
 *   global admin        -> the branch they have switched to, or "All branches"
 *
 * The switcher reloads the page when the selection changes, so reading the
 * value once on mount is sufficient.
 */
class BranchContextLabel extends Component {
  constructor(props) {
    super(props);
    this.state = {
      // Seeded from the login payload where available; resolved from the API
      // otherwise, so a session predating that field still shows a name.
      name: isBranchScopedAdmin() ? getUserBranchName() : null,
      resolved: false,
    };
  }

  componentDidMount() {
    const branchId = isBranchScopedAdmin() ? getUserBranchId() : getActiveBranchId();

    // A global admin with no branch selected is genuinely looking at all of
    // them — that is a real state, not a missing value.
    if (!branchId) {
      this.setState({ resolved: true });
      return;
    }
    if (this.state.name) {
      this.setState({ resolved: true });
      return;
    }

    getActiveBranches()
      .then((res) => {
        const list = res && res.data;
        if (!Array.isArray(list)) return this.setState({ resolved: true });
        const b = list.find((x) => String(x._id) === String(branchId));
        this.setState({ name: b ? b.name || b.code : null, resolved: true });
      })
      .catch(() => this.setState({ resolved: true }));
  }

  render() {
    const { name, resolved } = this.state;
    // Say nothing until we know, rather than flashing "All branches" at a
    // branch admin who is not looking at all branches.
    if (!resolved && !name) return null;

    // Global admins already have the switcher, which names the current
    // selection AND lets them change it. Printing the same words beside the
    // hotel name just says it twice. This label exists for branch-scoped
    // admins, who have no switcher at all.
    if (!isBranchScopedAdmin()) return null;
    if (!name) return null;

    return (
      <div
        className="d-none d-md-flex align-items-center px-3"
        title="You manage this branch only"
        style={{ whiteSpace: "nowrap", opacity: 0.85 }}
      >
        <i className="bx bx-store-alt mr-2" style={{ fontSize: "17px" }}></i>
        <span style={{ fontWeight: 600 }}>{name}</span>
      </div>
    );
  }
}

export default BranchContextLabel;
